import * as Yup from 'yup';
import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Duration, Sentry } from '@idk-web/core-utils';
import {
  BankIdQrDialog,
  AlternativeButton,
  Bold,
  Box,
  ConfirmDialog,
  DangerButton,
  downloadBlob,
  DownloadButton,
  ErrorDialog,
  hostname,
  loseFocus,
  ResponsiveImage,
  SecureButton,
  Sensitive,
  SignatureIcon,
  SpinningCircleLoader,
  StandardCard,
  Styling,
  useBreakPoint,
  useDialog,
  useDialog2,
  useIsMounted,
  useNotification,
  SelectorGroup,
  FrejaQrDialog,
} from '@idk-web/core-ui';
import {
  Country,
  EidProvider,
  deleteInboxMessage,
  downloadInboxTeleSignPdf,
  forwardInboxMessage,
  getInboxSignature,
} from '@idk-web/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons/faFilePdf';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons/faFileArrowDown';
import { translateError } from '@/utils/error';
import { useInboxUser } from '@/hooks/inbox/useInboxUser';
import { useInboxBrand } from '@/hooks/inbox/useInboxBrand';
import { useInboxSign } from '@/hooks/inbox/sign/useInboxSign';
import { DEFAULT_NOTIFICATION_AUTO_HIDE_MS } from '@/config';
import { useAttachments } from '@/components/inbox/safemail/useAttachments';
import { InboxMessageWithSigners } from '@/components/inbox/safemail/SafeMailInbox';
import PdfAttachment from '@/components/inbox/safemail/PdfAttachment';
import ImageAttachment from '@/components/inbox/safemail/ImageAttachment';
import EidProviderLabel from '@/components/common/visual/EidProviderLabel';

const Card = styled(StandardCard)`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: ${Styling.spacing(6)};
  @media (max-width: ${Styling.breakpoint('lg')}px) {
    padding: ${Styling.spacing(6, 4)};
  }
  @media (max-width: ${Styling.breakpoint('sm')}px) {
    padding: ${Styling.spacing(4, 2)};
  }
`;

const Content = styled(Box).attrs(({ theme }) => ({
  direction: 'vertical',
  spacing: theme.spacing[4],
  alignX: 'center',
}))``;

const MessageSection = styled(Box).attrs(({ theme }) => ({
  direction: 'vertical',
  alignX: 'center',
  spacing: theme.spacing[2],
}))``;

const MessageSubject = styled.h2`
  ${Styling.typography('h2')};
  color: ${({ theme }) => theme.palette.secondary.main};
  text-align: center;
`;

const MessageBody = styled.div`
  ${Styling.typography('body')};
  width: 100%;
  color: ${({ theme }) => theme.palette.primary.text};
  background-color: #ebf6fd;
  word-break: break-word;
  border-radius: 4px;
  padding: ${Styling.spacing(3)};

  & a {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const SignSection = styled(Box).attrs(({ theme }) => ({
  direction: 'vertical',
  alignX: 'center',
  spacing: theme.spacing[2],
}))``;

const SignersHeader = styled.h3`
  ${Styling.typography('h3')};
  color: ${({ theme }) => theme.palette.secondary.main};
  text-align: center;
`;

const Signer = styled(Box).attrs({
  direction: 'horizontal',
  alignX: 'center',
  alignY: 'center',
})``;

const DeletionMessage = styled.div`
  ${Styling.typography('body')};
  text-align: right;
  color: ${({ theme }) => theme.palette.grayscale.dark1};
`;

const BottomBar = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: auto auto;
  gap: ${Styling.spacing(4)};

  @media (max-width: ${Styling.breakpoint('md')}px) {
    grid-template-columns: auto;
  }
`;

const DeleteSection = styled(Box).attrs(({ theme }) => ({
  spacing: theme.spacing[1],
  direction: 'vertical',
}))``;

const Hash = styled.div`
  margin-top: 8px;
  word-break: break-all;
`;

const ButtonGroup = styled(Box).attrs(({ theme }) => ({
  spacing: theme.spacing[2],
}))`
  width: auto;
`;

const CompanyLogo = styled(ResponsiveImage).attrs({
  alt: '',
})`
  max-width: 160px;
  max-height: 120px;
`;

export type InboxMessageCardProps = {
  message: InboxMessageWithSigners;
  onSign?(): void;
  onForward?(): void;
  onDelete?(message: InboxMessageWithSigners): void;
};

function isAttachmentDisplayable(filename: string): boolean {
  return (
    filename.toLowerCase().endsWith('.pdf') ||
    filename.toLowerCase().endsWith('.png') ||
    filename.toLowerCase().endsWith('.jpg')
  );
}

const InboxMessageCard: FC<InboxMessageCardProps> = ({
  message,
  onSign,
  onForward,
  onDelete,
}) => {
  const breakpoint = useBreakPoint('md');
  const { t } = useTranslation();
  const mobile = useBreakPoint('md');
  const dialog = useDialog();
  const notification = useNotification();
  const isMounted = useIsMounted();
  const user = useInboxUser();
  const brand = useInboxBrand();
  const attachments = useAttachments(message);
  const [isForwarding, setIsForwarding] = useState(false);
  const inboxSign = useInboxSign();
  const availableProviders = useMemo(() => {
    let providers: EidProvider[];
    switch (user.country) {
      case Country.SWEDEN:
        providers = [EidProvider.BANKID, EidProvider.FREJA];
        break;
      case Country.NORWAY:
        providers = [EidProvider.BANKID_NO, EidProvider.FREJA];
        break;
      case Country.DENMARK:
        providers = [EidProvider.MITID, EidProvider.FREJA];
        break;
      case Country.FINLAND:
        providers = [EidProvider.FTN, EidProvider.FREJA];
        break;
      default:
        return [];
    }

    if (brand.brand && brand.brand.providers) {
      const currentBrand = brand.brand;

      return providers.filter((provider) =>
        currentBrand.providers.includes(provider),
      );
    }

    // Don't allow signing with Freja unless brand allows it.
    return providers.filter((provider) => provider !== EidProvider.FREJA);
  }, [brand]);
  const [provider, setProvider] = useState(availableProviders[0]);
  const qrDialog = useDialog2(
    (props) => {
      if (provider === EidProvider.BANKID) {
        return (
          <BankIdQrDialog
            data={inboxSign.state === 'WAITING' ? inboxSign.bankId : undefined}
            onCancel={() => {
              props.onClose?.();
              void inboxSign.cancel();
            }}
            {...props}
          />
        );
      }

      return (
        <FrejaQrDialog
          qrData={
            inboxSign.state === 'WAITING' ? inboxSign.freja?.qrData : undefined
          }
          onCancel={() => {
            props.onClose?.();
            void inboxSign.cancel();
          }}
          {...props}
        />
      );
    },
    { closeOnBackgroundClick: false, closeOnEscape: false },
    [provider, inboxSign],
  );
  const signFormik = useFormik({
    initialValues: {},
    validationSchema: Yup.object().shape({}),
    onSubmit() {
      loseFocus();

      switch (provider) {
        case EidProvider.BANKID:
          void inboxSign.signBankIdSe(message.id);
          return;
        case EidProvider.FREJA:
          void inboxSign.signFreja(message.id);
          return;
      }

      const modal = window.open(
        '',
        'idkSign',
        'width=600,height=625,toolbar=no,menubar=no,status=no',
      );

      if (modal) {
        switch (provider) {
          case EidProvider.BANKID_NO:
            void inboxSign.signBankIdNo(message.id, modal);
            break;
          case EidProvider.MITID:
            void inboxSign.signMitId(message.id, modal);
            break;
          case EidProvider.FTN:
            void inboxSign.signFtn(message.id, modal);
            break;
        }
      } else {
        dialog.show((props) => (
          <ErrorDialog
            {...props}
            text={t('login.error.failed_to_open_window')}
          />
        ));
      }
    },
  });
  const deletedInDays = useMemo(() => {
    const deletionDate = Date.parse(message.autodeleteDate);
    const duration = Duration.betweenTimestamps(
      Date.now() / 1000,
      deletionDate / 1000,
      ['days'],
    );

    return duration.days;
  }, [message]);

  useEffect(() => {
    if (inboxSign.state === 'FAILED') {
      dialog.show((props) => <ErrorDialog {...props} text={inboxSign.error} />);
    }

    if (inboxSign.state === 'TIMED_OUT') {
      dialog.show((props) => (
        <ErrorDialog
          {...props}
          header={t('error.timed_out.title')}
          text={t('error.timed_out.text')}
        />
      ));
    }

    if (inboxSign.state === 'COMPLETE') {
      notification.show(
        t('inbox.safemail.sign.success'),
        'success',
        DEFAULT_NOTIFICATION_AUTO_HIDE_MS,
      );

      onSign?.();
    }

    if (
      inboxSign.state === 'FAILED' ||
      inboxSign.state === 'CANCELLED' ||
      inboxSign.state === 'TIMED_OUT'
    ) {
      inboxSign.reset();
    }
  }, [inboxSign.state]);

  useEffect(() => {
    if (inboxSign.state !== 'WAITING') {
      qrDialog.close();
    }

    return () => qrDialog.close();
  }, [inboxSign.state]);

  useEffect(() => {
    if (
      [EidProvider.BANKID, EidProvider.FREJA].includes(provider) &&
      inboxSign.state === 'WAITING'
    ) {
      qrDialog.open({});

      return () => qrDialog.close();
    }
  }, [provider, inboxSign.state]);

  const handleSign = () => {
    signFormik.handleSubmit();
  };

  const handleForward = async () => {
    const handleOk = async () => {
      setIsForwarding(true);

      try {
        await forwardInboxMessage(message.id);

        notification.show(
          t('inbox.safemail.forward.success'),
          'success',
          DEFAULT_NOTIFICATION_AUTO_HIDE_MS,
        );

        onForward?.();
      } catch (e) {
        Sentry.reportError('Failed to forward end user inbox message', {
          error: e,
        });
        dialog.show((props) => (
          <ErrorDialog {...props} text={translateError(t, e)} />
        ));
      }

      if (isMounted()) {
        setIsForwarding(false);
      }
    };

    dialog.show((props) => (
      <ConfirmDialog
        {...props}
        header={t('common.are_you_sure')}
        text={t('inbox.safemail.forward.are_you_sure')}
        onOk={handleOk}
      />
    ));
  };

  const handleDelete = () => {
    const handleOk = async () => {
      try {
        await deleteInboxMessage(message.id);
        onDelete?.(message);

        notification.show(
          t('inbox.safemail.delete.success'),
          'success',
          DEFAULT_NOTIFICATION_AUTO_HIDE_MS,
        );
      } catch (e) {
        Sentry.reportError('Failed to delete end user inbox message', {
          error: e,
        });
        dialog.show((props) => (
          <ErrorDialog {...props} text={translateError(t, e)} />
        ));
      }
    };

    dialog.show((props) => (
      <ConfirmDialog
        {...props}
        header={t('common.are_you_sure')}
        text={t('inbox.safemail.delete.are_you_sure')}
        onOk={handleOk}
      />
    ));
  };

  const getSubject = (): string => {
    if (message.requestSignature) {
      if (message.zipCreated) {
        return t('inbox.safemail.sign.document_signed_by_all');
      } else {
        return t('inbox.safemail.sign.signature_request');
      }
    }

    return message.subject;
  };

  const handleDownloadSignature = async () => {
    try {
      const download = await getInboxSignature(message.id);
      downloadBlob(download, 'signature.zip');
    } catch (e) {
      Sentry.reportError('Failed to download end user inbox signature', {
        error: e,
      });
      dialog.show((props) => (
        <ErrorDialog {...props} text={translateError(t, e)} />
      ));
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const download = await downloadInboxTeleSignPdf(message.id);
      downloadBlob(download, `${user.displayName.replace(/\s+/g, '_')}.pdf`);
    } catch (e) {
      Sentry.reportError('Failed to download TeleSign PDF', { error: e });
      dialog.show((props) => (
        <ErrorDialog {...props} text={translateError(t, e)} />
      ));
    }
  };

  const canSign =
    !message.messageSigned &&
    Boolean(message.requestSignature) !== Boolean(message.zipCreated) &&
    hostname.tld === 'se';
  const canForward =
    Boolean(message.requestSignature) === Boolean(message.zipCreated);

  return (
    <>
      <Card>
        <Content>
          {message.companyLogoUrl && (
            <div>
              <CompanyLogo src={message.companyLogoUrl} />
            </div>
          )}
          <MessageSection>
            <MessageSubject>
              <Sensitive>{getSubject()}</Sensitive>
            </MessageSubject>
            {message.zipCreated ? (
              <div>
                {t('inbox.safemail.sign.signed_document_message')}
                &nbsp;
                {t('inbox.safemail.sign.signed_document_recommendation')}
                &nbsp;
                <Bold>
                  {t('inbox.safemail.sign.signed_document_autodelete', {
                    days: deletedInDays,
                  })}
                </Bold>
              </div>
            ) : (
              <MessageBody>
                <Sensitive>
                  <div
                    dangerouslySetInnerHTML={{ __html: message.body ?? '' }}
                  />
                  <div
                    dangerouslySetInnerHTML={{ __html: message.footer ?? '' }}
                  />
                </Sensitive>
              </MessageBody>
            )}
          </MessageSection>
          {message.signers && (
            <SignSection>
              <SignSection>
                <SignersHeader>
                  {t('inbox.safemail.sign.document_signers')}
                </SignersHeader>
                {message.signers.map((signer, index) => {
                  return (
                    <Signer key={`${signer.ssn}${index}`}>
                      <SignatureIcon signed={Boolean(signer.signatureDate)} />
                      {signer.signatureDate ? (
                        <Sensitive>
                          <p>
                            <Bold>{signer.displayName}</Bold>
                            {` - ${t('inbox.safemail.sign.signed')} ${new Date(
                              signer.signatureDate * 1000,
                            ).toLocaleString()}`}
                          </p>
                        </Sensitive>
                      ) : (
                        <Sensitive>
                          <p>
                            {signer.email} {signer.ssn}
                          </p>
                        </Sensitive>
                      )}
                    </Signer>
                  );
                })}
              </SignSection>
              {canSign && (
                <Box direction="vertical" alignX="center" spacing={16}>
                  {availableProviders.length > 1 && (
                    <Box style={{ width: 'auto' }}>
                      <SelectorGroup
                        value={provider}
                        disabled={inboxSign.state !== 'IDLE'}
                        options={availableProviders.map((value) => ({
                          label: <EidProviderLabel provider={value} />,
                          value,
                        }))}
                        onChange={(value) => setProvider(value as EidProvider)}
                        direction={mobile.below ? 'vertical' : 'horizontal'}
                      />
                    </Box>
                  )}
                  <Box direction="vertical" alignX="center" spacing="8px">
                    <SecureButton
                      disabled={
                        inboxSign.state === 'WAITING' ||
                        !signFormik.isValid ||
                        message.messageSigned
                      }
                      loading={
                        inboxSign.state === 'WAITING' && signFormik.isSubmitting
                      }
                      text={
                        message.messageSigned
                          ? t('inbox.safemail.sign.document_signed_by_user')
                          : t('inbox.safemail.sign.button')
                      }
                      onClick={handleSign}
                    />
                  </Box>
                </Box>
              )}
            </SignSection>
          )}
          <BottomBar>
            <Box
              alignX={breakpoint.above ? 'left' : 'center'}
              direction="vertical"
            >
              {message.signature && message.zipCreated && (
                <>
                  <DownloadButton
                    text={t('inbox.safemail.attachment.download_signature')}
                    startAdornment={
                      <FontAwesomeIcon icon={faFileArrowDown} size="lg" />
                    }
                    onClick={handleDownloadSignature}
                  />
                  {message.attachmentHash && (
                    <Hash>SHA256 hash: {message.attachmentHash}</Hash>
                  )}
                </>
              )}
              {message.isTeleSign && (
                <DownloadButton
                  text={t('inbox.safemail.download_pdf.button')}
                  startAdornment={
                    <FontAwesomeIcon icon={faFilePdf} size="lg" />
                  }
                  onClick={handleDownloadPdf}
                />
              )}
            </Box>
            <DeleteSection alignX={breakpoint.above ? 'right' : 'center'}>
              <ButtonGroup>
                {canForward && (
                  <AlternativeButton
                    disabled={isForwarding}
                    text={t('inbox.safemail.forward.button')}
                    onClick={handleForward}
                  />
                )}
                <DangerButton
                  text={t('common.delete')}
                  onClick={handleDelete}
                />
              </ButtonGroup>
              {message.autodeleteDate && (
                <DeletionMessage>
                  {t('inbox.safemail.message_will_autodelete')}{' '}
                  {new Date(message.autodeleteDate).toLocaleDateString()}.
                </DeletionMessage>
              )}
            </DeleteSection>
          </BottomBar>
        </Content>
      </Card>
      {attachments.map((attachment, i) => (
        <Card key={message.id + attachment.name}>
          <MessageSection>
            <MessageSubject>
              {attachment.name ?? `Attachment ${i}`}
            </MessageSubject>
            {(attachment.state === 'ERROR' ||
              (isAttachmentDisplayable(attachment.name) &&
                breakpoint.above)) && (
              <MessageBody>
                {attachment.state === 'LOADING' && <SpinningCircleLoader />}
                {
                  attachment.state === 'ERROR' &&
                    attachment.error /* TODO show nicer error */
                }
                {(() => {
                  const blob = attachment.asBlob();
                  const url = attachment.asURL();

                  if (blob && url) {
                    switch (blob.type) {
                      case 'application/pdf':
                        return <PdfAttachment file={blob} />;
                      case 'image/jpeg':
                      case 'image/png':
                        return (
                          <ImageAttachment
                            src={url}
                            alt={attachment.name ?? 'Attachment'}
                          />
                        );
                    }
                  }

                  return null;
                })()}
              </MessageBody>
            )}
            <Box
              key={message.id + attachment.name}
              alignX={breakpoint.above ? 'left' : 'center'}
            >
              <DownloadButton
                loading={attachment.state === 'LOADING'}
                text={t('inbox.safemail.attachment.download_attachment')}
                startAdornment={
                  <FontAwesomeIcon icon={faFileArrowDown} size="lg" />
                }
                onClick={() => attachment.download()}
              />
            </Box>
          </MessageSection>
        </Card>
      ))}
    </>
  );
};

export default InboxMessageCard;
